import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Save,
  TestTube,
  CheckCircle,
  XCircle,
  Loader2,
  Radio,
  Wifi,
  Server,
  Network,
  Database,
  Eye,
  EyeOff,
  RefreshCw,
  Key,
  Users,
} from 'lucide-react'
import { testTaranaConnection, testSplynxConnection, saveSettings, getSettings, syncBNMetadata } from '../services/api'
import ApiKeysPanel from '../components/ApiKeysPanel'
import UsersPanel from '../components/UsersPanel'
import { useRole } from '../lib/RoleContext'
import { hasRole } from '../lib/role'

interface ConnectionStatus {
  tested: boolean
  success: boolean
  message: string
}

type TabType = 'tarana' | 'positron' | 'remotewinbox' | 'smartolt' | 'splynx' | 'apikeys' | 'users'

const tabs: { id: TabType; label: string; icon: typeof Radio; enabled: boolean }[] = [
  { id: 'tarana', label: 'Tarana TCS', icon: Radio, enabled: true },
  { id: 'positron', label: 'Positron', icon: Server, enabled: false },
  { id: 'remotewinbox', label: 'RemoteWinbox', icon: Network, enabled: false },
  { id: 'smartolt', label: 'SmartOLT', icon: Wifi, enabled: false },
  { id: 'splynx', label: 'Splynx', icon: Database, enabled: true },
  { id: 'apikeys', label: 'API Keys', icon: Key, enabled: true },
  { id: 'users', label: 'Users', icon: Users, enabled: true },
]

export default function Settings() {
  const role = useRole()
  const [activeTab, setActiveTab] = useState<TabType>('tarana')

  // Tarana form state
  const [taranaApiKey, setTaranaApiKey] = useState('')
  const [taranaApiUrl, setTaranaApiUrl] = useState('https://api.tcs.taranawireless.com')
  const [taranaApiEnabled, setTaranaApiEnabled] = useState(true)
  const [showTaranaKey, setShowTaranaKey] = useState(false)

  // Splynx form state
  const [splynxUrl, setSplynxUrl] = useState('')
  const [splynxApiKey, setSplynxApiKey] = useState('')
  const [splynxApiSecret, setSplynxApiSecret] = useState('')
  const [showSplynxKey, setShowSplynxKey] = useState(false)
  const [showSplynxSecret, setShowSplynxSecret] = useState(false)

  // Sync settings
  const [syncFrequency, setSyncFrequency] = useState('60')
  const [roguePolicySla, setRoguePolicySla] = useState('sla-25')
  const [pruneThreshold, setPruneThreshold] = useState('30')

  // Connection test state
  const [taranaStatus, setTaranaStatus] = useState<ConnectionStatus | null>(null)
  const [splynxStatus, setSplynxStatus] = useState<ConnectionStatus | null>(null)
  const [saveStatus, setSaveStatus] = useState<ConnectionStatus | null>(null)
  const [bnSyncStatus, setBnSyncStatus] = useState<ConnectionStatus | null>(null)

  // Load settings from database
  const { data: savedSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await getSettings()
      return response.data
    },
  })

  // Populate form when settings load
  useEffect(() => {
    if (savedSettings) {
      setTaranaApiUrl(savedSettings.tarana_api_url || 'https://api.tcs.taranawireless.com')
      setTaranaApiKey(savedSettings.tarana_api_key || '')
      setTaranaApiEnabled(savedSettings.tarana_api_enabled ?? true)
      setSplynxUrl(savedSettings.splynx_url || '')
      setSplynxApiKey(savedSettings.splynx_api_key || '')
      setSplynxApiSecret(savedSettings.splynx_api_secret || '')
      setSyncFrequency(String(savedSettings.sync_frequency || 60))
      setRoguePolicySla(savedSettings.rogue_policy_sla || 'sla-25')
      setPruneThreshold(String(savedSettings.prune_threshold || 30))
    }
  }, [savedSettings])

  // Test mutations
  const testTarana = useMutation({
    mutationFn: () => testTaranaConnection({
      api_key: taranaApiKey || undefined,
      api_url: taranaApiUrl || undefined,
    }),
    onSuccess: (response) => {
      setTaranaStatus({
        tested: true,
        success: response.data.success,
        message: response.data.message,
      })
    },
    onError: (error: Error) => {
      setTaranaStatus({
        tested: true,
        success: false,
        message: error.message,
      })
    },
  })

  const testSplynx = useMutation({
    mutationFn: () => testSplynxConnection({
      url: splynxUrl || undefined,
      api_key: splynxApiKey || undefined,
      api_secret: splynxApiSecret || undefined,
    }),
    onSuccess: (response) => {
      setSplynxStatus({
        tested: true,
        success: response.data.success,
        message: response.data.message,
      })
    },
    onError: (error: Error) => {
      setSplynxStatus({
        tested: true,
        success: false,
        message: error.message,
      })
    },
  })

  const syncBNs = useMutation({
    mutationFn: () => syncBNMetadata(),
    onSuccess: (response) => {
      const data = response.data
      setBnSyncStatus({
        tested: true,
        success: data.failed === 0,
        message: `Synced ${data.updated} BN(s) successfully${data.failed > 0 ? `, ${data.failed} failed` : ''}`,
      })
      setTimeout(() => setBnSyncStatus(null), 5000)
    },
    onError: (error: Error) => {
      setBnSyncStatus({
        tested: true,
        success: false,
        message: error.message || 'Failed to sync BNs',
      })
    },
  })

  const queryClient = useQueryClient()

  const saveMutation = useMutation({
    mutationFn: () => saveSettings({
      tarana_api_key: taranaApiKey || undefined,
      tarana_api_url: taranaApiUrl || undefined,
      tarana_api_enabled: taranaApiEnabled,
      splynx_url: splynxUrl || undefined,
      splynx_api_key: splynxApiKey || undefined,
      splynx_api_secret: splynxApiSecret || undefined,
      sync_frequency: parseInt(syncFrequency) || undefined,
      rogue_policy_sla: roguePolicySla || undefined,
      prune_threshold: parseInt(pruneThreshold) || undefined,
    }),
    onSuccess: (response) => {
      setSaveStatus({
        tested: true,
        success: response.data.success,
        message: response.data.message,
      })
      // Refresh settings from DB to reload saved values
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setTimeout(() => setSaveStatus(null), 3000)
    },
    onError: (error: Error) => {
      setSaveStatus({
        tested: true,
        success: false,
        message: error.message,
      })
    },
  })

  const handleSave = () => {
    saveMutation.mutate()
  }

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (!hasRole(role, 'admin')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm border border-gray-200 max-w-sm">
          <p className="text-gray-700 font-medium">Admins only — you don't have access to Settings.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <div className="flex items-center space-x-4">
          {saveStatus && (
            <div className="flex items-center">
              {saveStatus.success ? (
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500 mr-2" />
              )}
              <span
                className={
                  saveStatus.success ? 'text-green-600' : 'text-red-600'
                }
              >
                {saveStatus.message}
              </span>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Equipment Type Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => tab.enabled && setActiveTab(tab.id)}
                  disabled={!tab.enabled}
                  className={`
                    flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors
                    ${activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : tab.enabled
                        ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        : 'border-transparent text-gray-300 cursor-not-allowed'
                    }
                  `}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                  {!tab.enabled && (
                    <span className="ml-2 text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
                      Coming Soon
                    </span>
                  )}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Tarana Settings */}
          {activeTab === 'tarana' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Tarana TCS API Connection
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Connect to Tarana Tower Cloud Service to manage your wireless radios.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API URL
                    </label>
                    <input
                      type="text"
                      value={taranaApiUrl}
                      onChange={(e) => setTaranaApiUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showTaranaKey ? 'text' : 'password'}
                        value={taranaApiKey}
                        onChange={(e) => setTaranaApiKey(e.target.value)}
                        placeholder="Enter Tarana API key"
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTaranaKey(!showTaranaKey)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showTaranaKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={taranaApiEnabled}
                        onChange={(e) => setTaranaApiEnabled(e.target.checked)}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700">Enable Tarana API calls</span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          When disabled, the system will use cached telemetry data instead of making API calls.
                          This helps preserve your monthly 10,000 call quota.
                        </p>
                      </div>
                    </label>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => testTarana.mutate()}
                      disabled={testTarana.isPending}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      {testTarana.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <TestTube className="h-4 w-4 mr-2" />
                      )}
                      Test Connection
                    </button>
                    {taranaStatus && (
                      <div className="flex items-center">
                        {taranaStatus.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500 mr-2" />
                        )}
                        <span
                          className={
                            taranaStatus.success ? 'text-green-600' : 'text-red-600'
                          }
                        >
                          {taranaStatus.message}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => syncBNs.mutate()}
                      disabled={syncBNs.isPending}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      {syncBNs.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Sync BN Metadata
                    </button>
                    {bnSyncStatus && (
                      <div className="flex items-center">
                        {bnSyncStatus.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500 mr-2" />
                        )}
                        <span
                          className={
                            bnSyncStatus.success ? 'text-green-600' : 'text-red-600'
                          }
                        >
                          {bnSyncStatus.message}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Fetch base node details (azimuth, carrier frequencies) from Tarana API.
                    This is a low-volume operation that should only be run when BNs are added or modified.
                  </p>
                </div>
              </div>

              <hr className="border-gray-200" />

              {/* Sync Settings for Tarana */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Sync Configuration
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Scheduled Sync Frequency
                    </label>
                    <select
                      value={syncFrequency}
                      onChange={(e) => setSyncFrequency(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="15">Every 15 minutes</option>
                      <option value="30">Every 30 minutes</option>
                      <option value="60">Every hour</option>
                      <option value="360">Every 6 hours</option>
                      <option value="1440">Daily</option>
                      <option value="0">Disabled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rogue Device Policy
                    </label>
                    <select
                      value={roguePolicySla}
                      onChange={(e) => setRoguePolicySla(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="sla-5">Set to sla-5 (5 Mbps)</option>
                      <option value="sla-25">Set to sla-25 (25 Mbps)</option>
                      <option value="sla-50">Set to sla-50 (50 Mbps)</option>
                      <option value="sla-100">Set to sla-100 (100 Mbps)</option>
                      <option value="max">Leave at max (no change)</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500">
                      SLA profile to apply to devices not assigned to any customer
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prune Threshold (days)
                    </label>
                    <input
                      type="number"
                      value={pruneThreshold}
                      onChange={(e) => setPruneThreshold(e.target.value)}
                      min="1"
                      max="365"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Remove radios from Tarana TCS after this many days disconnected
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Positron Settings (Coming Soon) */}
          {activeTab === 'positron' && (
            <div className="text-center py-12">
              <Server className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Positron Integration
              </h3>
              <p className="text-gray-500">
                Positron access switch integration is coming soon.
              </p>
            </div>
          )}

          {/* RemoteWinbox Settings (Coming Soon) */}
          {activeTab === 'remotewinbox' && (
            <div className="text-center py-12">
              <Network className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                RemoteWinbox Integration
              </h3>
              <p className="text-gray-500">
                MikroTik RemoteWinbox integration is coming soon.
              </p>
            </div>
          )}

          {/* SmartOLT Settings (Coming Soon) */}
          {activeTab === 'smartolt' && (
            <div className="text-center py-12">
              <Wifi className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                SmartOLT Integration
              </h3>
              <p className="text-gray-500">
                SmartOLT fiber management integration is coming soon.
              </p>
            </div>
          )}

          {/* Splynx Settings */}
          {activeTab === 'splynx' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Splynx API Connection
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Connect to your Splynx instance to sync customer and equipment data.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Splynx URL
                    </label>
                    <input
                      type="text"
                      value={splynxUrl}
                      onChange={(e) => setSplynxUrl(e.target.value)}
                      placeholder="https://your-splynx.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showSplynxKey ? 'text' : 'password'}
                          value={splynxApiKey}
                          onChange={(e) => setSplynxApiKey(e.target.value)}
                          placeholder="Enter API key"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSplynxKey(!showSplynxKey)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSplynxKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showSplynxSecret ? 'text' : 'password'}
                          value={splynxApiSecret}
                          onChange={(e) => setSplynxApiSecret(e.target.value)}
                          placeholder="Enter API secret"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSplynxSecret(!showSplynxSecret)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSplynxSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => testSplynx.mutate()}
                      disabled={testSplynx.isPending}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      {testSplynx.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <TestTube className="h-4 w-4 mr-2" />
                      )}
                      Test Connection
                    </button>
                    {splynxStatus && (
                      <div className="flex items-center">
                        {splynxStatus.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500 mr-2" />
                        )}
                        <span
                          className={
                            splynxStatus.success ? 'text-green-600' : 'text-red-600'
                          }
                        >
                          {splynxStatus.message}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Keys */}
          {activeTab === 'apikeys' && <ApiKeysPanel />}

          {/* Users */}
          {activeTab === 'users' && <UsersPanel />}
        </div>
      </div>
    </div>
  )
}
